/**
 * Frontend environment configuration with Zod runtime validation.
 *
 * Validates all VITE_* env vars at app startup.
 * Critical vars (API_URL, ADMIN_ADDRESS) cause a hard error;
 * optional vars (SENTRY, PostHog) log a warning and default to empty.
 */

import { z } from "zod";

const envSchema = z.object({
  VITE_API_URL: z.string().url("VITE_API_URL must be a valid URL"),
  VITE_TON_NETWORK: z.enum(["mainnet", "testnet"]).default("testnet"),
  VITE_TONCONNECT_MANIFEST_URL: z.string().url().optional(),
  VITE_ADMIN_ADDRESS: z.string().min(1, "VITE_ADMIN_ADDRESS is required"),
  VITE_BOT_USERNAME: z.string().default("USPBetBot"),

  // Observability — optional, gracefully degraded
  VITE_SENTRY_DSN: z.string().optional().default(""),
  VITE_POSTHOG_KEY: z.string().optional().default(""),
  VITE_POSTHOG_HOST: z.string().optional().default("https://us.i.posthog.com"),
});

export type EnvConfig = z.infer<typeof envSchema>;

function validateEnv(): EnvConfig {
  const raw = {
    VITE_API_URL: import.meta.env['VITE_API_URL'],
    VITE_TON_NETWORK: import.meta.env['VITE_TON_NETWORK'],
    VITE_TONCONNECT_MANIFEST_URL: import.meta.env['VITE_TONCONNECT_MANIFEST_URL'],
    VITE_ADMIN_ADDRESS: import.meta.env['VITE_ADMIN_ADDRESS'],
    VITE_BOT_USERNAME: import.meta.env['VITE_BOT_USERNAME'],
    VITE_SENTRY_DSN: import.meta.env['VITE_SENTRY_DSN'],
    VITE_POSTHOG_KEY: import.meta.env['VITE_POSTHOG_KEY'],
    VITE_POSTHOG_HOST: import.meta.env['VITE_POSTHOG_HOST'],
  };

  const result = envSchema.safeParse(raw);

  if (!result.success) {
    const errors = result.error.flatten().fieldErrors;
    console.error("Invalid environment variables:", errors);

    if (import.meta.env.PROD) {
      throw new Error(`Missing required env vars: ${Object.keys(errors).join(", ")}`);
    }
  }

  // In dev, return a partial config to avoid blocking local iteration
  return (result.success ? result.data : envSchema.parse({
    ...raw,
    VITE_API_URL: raw.VITE_API_URL || "http://localhost:3000/api",
    VITE_ADMIN_ADDRESS: raw.VITE_ADMIN_ADDRESS || "PLACEHOLDER",
  })) as EnvConfig;
}

export const env = validateEnv();
